<template>
  <page-wrapper sticky-second-row>
    <template #title>
      <div class="h3">Поддержка</div>
    </template>
    <div class="paragraph-m_h">
      <div>Мы&nbsp;подготовили для вас список самых распространенных вопросов наших пользователей.</div>
      <div class="mt-2">
        Если ваш вопрос решается только через обращение в&nbsp;Поддержку, то&nbsp;вы&nbsp;можете написать в&nbsp;<span
          class="link"
          @click="openChat"
          >онлайн-чат</span
        >
      </div>
    </div>

    <div class="h4 mt-6">Часто задаваемые вопросы</div>

    <div class="d-flex align-start">
      <div class="mr-md-10">
        <div
          v-for="(section, sectionIndex) in faq"
          :key="section.sectionTitle"
          class="mt-6 d-flex flex-column align-start"
        >
          <div class="paragraph-l font-600" v-html="section.sectionTitle" />
          <div
            v-for="(question, index) in section.questions"
            :key="question.question"
            class="d-flex align-start paragraph-m_h font-600 mt-4 link"
            @click="openQuestion(sectionIndex, index)"
          >
            <div
              v-if="$vuetify.display.smAndDown && activeQuestion && isActiveQuestion(sectionIndex, index)"
              class="support__active-question"
            >
              <div class="d-flex align-center justify-space-between">
                <div class="paragraph-m_h font-600" v-html="activeQuestion.question" />
                <e-icon
                  name="closeSvg"
                  class="text-secondary cursor-pointer ml-4"
                  :size="16"
                  @click.stop="closeQuestion"
                />
              </div>
              <div class="paragraph-m_h mt-3 font-400" v-html="activeQuestion.answer" />
            </div>

            <template v-else>
              <v-img
                :src="isActiveQuestion(sectionIndex, index) ? QuestionFullIcon : QuestionIcon"
                width="24"
                class="flex-shrink-0"
              />
              <div
                class="h6 ml-4 support__question"
                :class="{ active: isActiveQuestion(sectionIndex, index) }"
                v-html="question.question"
              />
            </template>
          </div>
        </div>
      </div>

      <div v-if="$vuetify.display.mdAndUp && activeQuestion" class="support__active-question">
        <div class="d-flex align-center justify-space-between">
          <div class="paragraph-m_h font-600" v-html="activeQuestion.question" />
          <e-icon name="closeSvg" class="text-secondary cursor-pointer ml-4" :size="16" @click="closeQuestion" />
        </div>
        <div class="paragraph-m_h mt-4" v-html="activeQuestion.answer" />
      </div>
    </div>

    <div class="paragraph-m pb-4 mt-10"><b>Не нашли ответов?</b> Свяжитесь с нами любым удобным для вас способом:</div>
    <div class="d-flex flex-wrap align-center row-gap-2 col-gap-10">
      <div class="d-flex pt-2 pb-2 pr-6 pr-sm-0 mr-10">
        <div class="pr-3">
          <v-img :src="iconChat" width="40" />
        </div>
        <div class="paragraph-m_h">
          <p>Сообщение в</p>
          <span rel="noopener noreferrer" class="font-600 link" @click="openChat"> online-чат </span>
        </div>
      </div>
      <div class="d-flex pt-2 pb-2 pr-6 pr-sm-0 mr-10">
        <div class="pr-3">
          <v-img :src="iconEmail" width="40" />
        </div>
        <div class="paragraph-m_h">
          <p>Обращение на почту</p>
          <a href="mailto:extranet@alean.ru" rel="noopener noreferrer" class="link font-600"> extranet@alean.ru </a>
        </div>
      </div>
      <div class="d-flex pt-2 pb-2 pr-6 pr-sm-0 mr-10">
        <div class="pr-3">
          <v-img :src="iconPhone" width="40" />
        </div>
        <div class="paragraph-m_h">
          <p>Звонок по телефону</p>
          <a href="tel:+74956466500" rel="noopener noreferrer" class="link font-600"> +7(495) 646 65 00 </a>
        </div>
      </div>
      <!-- <div class="d-flex pt-2 pb-2 pr-6 pr-sm-0">
        <div class="pr-3">
          <v-img :src="iconTelegram" width="40" />
        </div>
        <div class="paragraph-m_h">
          <p>Сообщение в телеграм</p>
          <a href="https://t.me/extranet_alean" rel="noopener noreferrer" class="link font-600"> @extranet_alean </a>
        </div>
      </div> -->
    </div>
  </page-wrapper>
</template>

<script setup lang="ts">
import { ref, toRef } from 'vue';
import iconChat from '@/assets/img/pages/support/chat.svg';
import iconEmail from '@/assets/img/pages/support/email.svg';
import iconPhone from '@/assets/img/pages/support/phone.svg';
import QuestionFullIcon from '@/assets/img/pages/support/questions-circle-full.svg';
import QuestionIcon from '@/assets/img/pages/support/questions-circle.svg';
// import iconTelegram from '@/assets/img/pages/support/telegram.svg';
import PageWrapper from '@/core/components/interface/PageWrapper.vue';
import { EIcon } from '@/core/components/ui';
import { useBitrixWidget } from '@/core/composables/bitrixWidget';
import { userFaq } from '@/parts/support/utils';

const { faq } = userFaq();

const { openChat } = useBitrixWidget();

const activeQuestionIndex = ref<{ sectionIndex: number; index: number } | null>(null);

const activeQuestion = toRef(() => {
  if (!activeQuestionIndex.value) {
    return null;
  }
  return faq[activeQuestionIndex.value.sectionIndex].questions[activeQuestionIndex.value.index];
});

const isActiveQuestion = (sectionIndex: number, index: number) =>
  activeQuestionIndex.value?.sectionIndex === sectionIndex && activeQuestionIndex.value?.index === index;

const openQuestion = (sectionIndex: number, index: number) => {
  activeQuestionIndex.value = { sectionIndex, index };
};

const closeQuestion = () => {
  activeQuestionIndex.value = null;
};
</script>

<style lang="scss" scoped>
.support {
  &__active-question {
    position: sticky;
    top: 16px;
    right: 0;

    flex-basis: 49%;

    max-width: 744px;
    margin-left: auto;
    padding: 24px;

    color: $text-primary-color;

    border: 1px solid $primary-color;
    border-radius: $border-radius;

    @include sm-and-down {
      position: static;

      flex-basis: 100%;

      max-width: 100%;
      padding: 16px;
    }
  }

  &__question {
    transition: color $transition-duration;

    &.active {
      color: $btn-primary-pressed-color;
    }
  }
}
</style>
